import React from "react";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  doc,
  setDoc,
  serverTimestamp,
  getDoc,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import firebaseApp from "../../firebase/credenciales";
import { createSGIHeader } from "../../utils/createSGIHeader";
import { Button, Spinner, ProgressBar } from "react-bootstrap";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const db = getFirestore(firebaseApp);
const storage = getStorage(firebaseApp);

// --- FILTRO PARA PREFERIR REGULARIZACIONES ---
const filtrarPreferirRegularizacion = (registros) => {
  const map = new Map();
  registros.forEach((r) => {
    const fechaKey = r.fechaJornada || r.fecha || (r.timestamp?.toDate ? r.timestamp.toDate().toISOString().slice(0, 10) : "");
    const key = `${r.dni || ""}-${fechaKey}-${r.tipo || ""}`;
    if (!map.has(key)) {
      map.set(key, r);
    } else {
      const actual = map.get(key);
      const esReg = r.esRegularizacion === true || r.origen === "regularizacion-aprobada";
      const actualEsReg = actual.esRegularizacion === true || actual.origen === "regularizacion-aprobada";
      if (esReg && !actualEsReg) {
        map.set(key, r);
      }
    }
  });
  return Array.from(map.values());
};

// Cache en memoria para datos de empresa para evitar consultas N+1
const companyCache = new Map();

// ✅ Función para obtener datos de la empresa con caché
const obtenerDatosEmpresa = async (ruc, cache = companyCache) => {
  try {
    if (!ruc) {
      console.log("No se proporcionó un RUC para buscar datos de la empresa.");
      return null;
    }
    if (cache && cache.has(ruc)) {
      return cache.get(ruc);
    }
    const companyRef = collection(db, "company");
    const q = query(companyRef, where("ruc", "==", ruc));
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      const data = snapshot.docs[0].data();
      if (cache) cache.set(ruc, data);
      return data;
    }
    const companyDocRef = doc(db, "company", ruc);
    const companyDoc = await getDoc(companyDocRef);
    if (companyDoc.exists()) {
      const data = companyDoc.data();
      if (cache) cache.set(ruc, data);
      return data;
    }
    return null;
  } catch (error) {
    console.error("Error al obtener datos de empresa:", error);
    return null;
  }
};

// ✅ Función para obtener firma del trabajador (sin cambios)
const obtenerFirmaTrabajador = async (dni) => {
  try {
    const q = query(
      collection(db, "firmas"),
      where("dni", "==", dni),
      where("activo", "==", true)
    );
    const snapshot = await getDocs(q);
    return !snapshot.empty ? snapshot.docs[0].data().link : null;
  } catch (error) {
    console.error("Error al obtener firma del trabajador:", error);
    return null;
  }
};

// --- MODIFICACIÓN EN obtenerRegistrosMensuales ---
const obtenerRegistrosMensuales = async (dni, año, mes) => {
  try {
    const registrosPorFecha = {};
    const mesFormateado = mes.toString().padStart(2, "0");
    const registrosRef = collection(db, `asistencia/${dni}/registros`);
    const q = query(
      registrosRef,
      where("ano", "==", año.toString()),
      where("periodo", "==", mesFormateado)
    );
    const snapshot = await getDocs(q);
    let registros = [];
    snapshot.forEach((doc) => {
      registros.push({
        id: doc.id,
        ...doc.data(),
      });
    });
    const ausenciasRef = collection(db, `asistencia/${dni}/ausencias`);
    const qAusencias = query(
      ausenciasRef,
      where("ano", "==", año.toString()),
      where("periodo", "==", mesFormateado)
    );
    const ausenciasSnapshot = await getDocs(qAusencias);
    ausenciasSnapshot.forEach((doc) => {
      registros.push({
        id: doc.id,
        esAusencia: true,
        ...doc.data(),
      });
    });

    // --- FILTRAR REGISTROS PARA PREFERIR REGULARIZACIONES ---
    registros = filtrarPreferirRegularizacion(registros);

    registros.forEach((registro) => {
      let fecha = registro.fechaJornada || registro.fecha;
      if (!fecha && registro.timestamp) {
        let fechaObj;
        if (typeof registro.timestamp.toDate === "function") {
          fechaObj = registro.timestamp.toDate();
        } else if (registro.timestamp instanceof Date) {
          fechaObj = registro.timestamp;
        } else {
          try {
            fechaObj = new Date(registro.timestamp);
          } catch (error) {
            return;
          }
        }
        const year = fechaObj.getFullYear();
        const month = (fechaObj.getMonth() + 1).toString().padStart(2, "0");
        const day = fechaObj.getDate().toString().padStart(2, "0");
        fecha = `${year}-${month}-${day}`;
      }
      if (fecha && fecha.startsWith(`${año}-${mesFormateado}`)) {
        if (!registrosPorFecha[fecha]) {
          registrosPorFecha[fecha] = [];
        }
        registrosPorFecha[fecha].push(registro);
      }
    });
    return registrosPorFecha;
  } catch (error) {
    console.error("Error al obtener registros mensuales:", error);
    return {};
  }
}

// ✅ Función para obtener días del mes (optimizada con objeto Date nativo)
const obtenerDiasDelMes = (año, mes) => {
  const añoNum = parseInt(año, 10);
  const mesNum = parseInt(mes, 10);
  const mesFmt = mesNum.toString().padStart(2, "0");
  const diasEnMes = new Date(añoNum, mesNum, 0).getDate();

  const result = [];
  for (let dia = 1; dia <= diasEnMes; dia++) {
    const diaFmt = dia.toString().padStart(2, "0");
    result.push(`${añoNum}-${mesFmt}-${diaFmt}`);
  }
  return result;
};

// ✅ Función para obtener nombre del mes (sin cambios)
const getNombreMes = (mes) => {
  const meses = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];
  return meses[mes - 1];
};

// ✅ Función para formatear hora (sin cambios)
const formatearHora = (timestamp) => {
  if (!timestamp) return "";
  let fecha;
  if (typeof timestamp === "string" && timestamp.includes(":")) {
    return timestamp.substring(0, 5);
  } else if (typeof timestamp.toDate === "function") {
    fecha = timestamp.toDate();
  } else {
    try {
      fecha = new Date(timestamp);
    } catch (error) {
      return "";
    }
  }
  const horas = fecha.getHours().toString().padStart(2, "0");
  const minutos = fecha.getMinutes().toString().padStart(2, "0");
  return `${horas}:${minutos}`;
};

// ✅ Función para obtener sede principal (sin cambios)
function obtenerSedePrincipal(registrosPorFecha) {
  const conteoCentros = {};
  Object.values(registrosPorFecha).forEach((registros) => {
    registros.forEach((reg) => {
      let nombre = reg.centroNombre;
      if (
        (!nombre || typeof nombre !== "string" || nombre.trim() === "") &&
        reg.centro &&
        typeof reg.centro.centroNombre === "string"
      ) {
        nombre = reg.centro.centroNombre;
      }
      if (nombre && typeof nombre === "string" && nombre.trim() !== "") {
        nombre = nombre.trim();
        conteoCentros[nombre] = (conteoCentros[nombre] || 0) + 1;
      }
    });
  });
  let sedePrincipal = "-";
  let max = 0;
  Object.entries(conteoCentros).forEach(([nombre, cantidad]) => {
    if (cantidad > max) {
      sedePrincipal = nombre;
      max = cantidad;
    }
  });
  return sedePrincipal;
}

// ✅ Función para crear HTML (sin cambios, pero adaptada para web)
export const createHTML = async (
  userData,
  registrosPorFecha,
  año,
  mes,
  firmaUrl,
  companyData
) => {
  const headerSGI = await createSGIHeader(companyData?.ruc, "3", companyData);
  const fechasDelMes = obtenerDiasDelMes(parseInt(año), parseInt(mes));
  const razonSocial = companyData?.name || "Empresa no especificada";
  const rucEmpresa = companyData?.ruc || userData?.rucEmpresa || "-";

  const cleanStr = (val) => {
    const s = String(val || "").trim();
    return s && s.toLowerCase() !== "n/a" && s.toLowerCase() !== "undefined" && s.toLowerCase() !== "null" ? s : "";
  };

  const nombres = cleanStr(userData?.nombres) || cleanStr(userData?.name) || cleanStr(userData?.nombre);
  const apellidos = cleanStr(userData?.apellidos) || `${cleanStr(userData?.apellidoPaterno)} ${cleanStr(userData?.apellidoMaterno)}`.trim();

  const nombreEmpleado = (apellidos && nombres)
    ? `${apellidos}, ${nombres}`
    : (apellidos || nombres || `Trabajador DNI: ${userData?.dni || "-"}`);

  const dniEmpleado = userData?.dni || "";
  const periodoReporte = `${getNombreMes(parseInt(mes))} ${año}`;
  const sedePrincipal = obtenerSedePrincipal(registrosPorFecha);

  return `
  <!DOCTYPE html>
  <html>
  <head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
<style>
  @page {
    size: A4 portrait;
    margin: 10mm;
  }
  * { box-sizing: border-box; }
  .pdf-body { 
    font-family: Arial, sans-serif; 
    padding: 10px; 
    width: 210mm; 
    height: 297mm; 
    margin: 0; 
    font-size: 10px; 
  }
  .container { 
    width: 100%; 
    max-width: 190mm; 
    margin: 0 auto; 
  }
  .sgi-header table { 
    width: 100%; 
    border-collapse: collapse; 
    font-size: 10px; 
    font-family: Arial, sans-serif; 
  }
  h1 { 
    text-align: center; 
    font-size: 14px; 
    margin-bottom: 5px; 
    font-weight: bold; 
  }
  h2 { 
    text-align: center; 
    font-size: 12px; 
    margin: 5px 0 15px 0; 
  }
  .info-table { 
    width: 100%; 
    margin: 0 auto 15px auto; 
    border-collapse: collapse; 
    border: 1px solid #000; 
  }
  .info-table th, .info-table td { 
    border: 1px solid #000; 
    padding: 5px; 
    font-size: 10px; 
  }
  .info-table th { 
    background-color: #f2f2f2; 
    font-weight: bold; 
    text-align: center; 
    width: 25%; 
  }
  .info-table { 
    margin-top: 20px; 
  }
  .registro-table { 
    width: 100% !important; 
    margin: 0 auto; 
    border-collapse: collapse; 
    margin-top: 10px; 
    table-layout: fixed; 
  }
  .registro-table th, .registro-table td { 
    border: 1px solid #000; 
    padding: 3px; 
    text-align: center; 
    font-size: 8px; 
    overflow: hidden; 
    white-space: normal; 
    word-wrap: break-word; 
    word-break: break-word; 
    vertical-align: top; 
    height: auto; 
  }
  .registro-table th { 
    background-color: #f2f2f2; 
    font-weight: bold; 
    white-space: nowrap; 
  }
  .registro-table th.subheader { 
    background-color: #e0e0e0; 
  }
  
  
  /* Anchos específicos para cada columna - TOTAL: 100% */
  .registro-table th:nth-child(1), .registro-table td:nth-child(1) { 
    width: 2% !important; 
  }
  .registro-table th:nth-child(2), .registro-table td:nth-child(2) { 
    width: 6% !important; 
  }
  .registro-table th:nth-child(3), .registro-table td:nth-child(3) { 
    width: 6% !important; 
  }
  .registro-table th:nth-child(4), .registro-table td:nth-child(4) { 
    width: 8% !important; 
  }
  .registro-table th:nth-child(5), .registro-table td:nth-child(5) { 
    width: 8% !important; 
  }
  .registro-table th:nth-child(6), .registro-table td:nth-child(6) { 
    width: 8% !important; 
  }
  .registro-table th:nth-child(7), .registro-table td:nth-child(7) { 
    width: 12% !important; 
  }
  .registro-table th:nth-child(8), .registro-table td:nth-child(8) { 
    width: 12% !important; 
  }
  .registro-table th:nth-child(9), .registro-table td:nth-child(9) { 
    width: 19% !important; 
  }
  .registro-table th:nth-child(10), .registro-table td:nth-child(10) {
    width: 19% !important; 
    text-align: left; 
    padding: 3px 5px; 
    line-height: 1.2; 
  }
  
  
  .firma-img { 
    width: 60px; 
    height: 20px; 
    display: block; 
    margin: 0 auto; 
  }
  .footer { 
    width: 100%; 
    margin-top: 15px; 
    font-size: 9px; 
    text-align: center; 
    border-top: 1px solid #ddd; 
    padding-top: 5px; 
  }
  .descanso { 
    background-color: #f5f5f5; 
  }
</style>
  </head>
  <body class="pdf-body">
    <div class="container">
      <div class="sgi-header">
        ${headerSGI}
      </div>
      <table class="info-table">
        <tr>
          <th>Empresa</th>
          <td>${razonSocial}</td>
          <th>RUC</th>
          <td>${rucEmpresa}</td>
        </tr>
        <tr>
          <th>Trabajador</th>
          <td>${nombreEmpleado}</td>
          <th>DNI</th>
          <td>${dniEmpleado}</td>
        </tr>
        <tr>
          <th>Centro laboral</th>
          <td>${sedePrincipal}</td>
          <th>Periodo</th>
          <td>${periodoReporte}</td>
        </tr>
      </table>
      <div style="width: 100%; overflow-x: auto;">
<table class="registro-table" cellspacing="0" cellpadding="0" border="1">
  <thead>
    <tr>
      <th rowspan="2">N°</th>
      <th rowspan="2">Fecha</th>
      <th rowspan="2">Hora Entrada</th>
      <th rowspan="2">Firma</th>
      <th rowspan="2">Hora Salida</th>
      <th rowspan="2">Firma</th>
      <th colspan="3">Hora de Refrigerio</th>
      <th rowspan="2">Observaciones</th>
    </tr>
    <tr>
      <th class="subheader">HInicio</th>
      <th class="subheader">HFinal</th>
      <th class="subheader">Firma</th>
    </tr>
  </thead>
  <tbody>

            
            ${fechasDelMes
              .map((fecha, index) => {
                const registrosDia = registrosPorFecha[fecha] || [];
                const registroDescanso = registrosDia.find(
                  (r) => r.tipo === "Descanso"
                );
                const esDescanso = registroDescanso !== undefined;
                const registroIngreso = esDescanso
                  ? null
                  : registrosDia.find((r) => r.tipo === "Ingreso");
                const registroSalida = esDescanso
                  ? null
                  : registrosDia.find((r) => r.tipo === "Salida");
                const registroAusencia = esDescanso
                  ? null
                  : registrosDia.find(
                      (r) => r.tipo === "Ausencia" || r.esAusencia
                    );
                const registroRefrigerioInicio = esDescanso
                  ? null
                  : registrosDia.find((r) => r.tipo === "Refrigerio_Inicio");
                const registroRefrigerioFin = esDescanso
                  ? null
                  : registrosDia.find((r) => r.tipo === "Refrigerio_Fin");
                const estiloFila = esDescanso ? 'class="descanso"' : "";
                const fechaObj2 = new Date(fecha + "T00:00:00");
                const diaFecha = fechaObj2.getDate();
                const nombreDia = format(fechaObj2, "EEE", {
                  locale: es,
                }).toUpperCase();

                // Formatear la fecha como DD/MM/YY (DIA)
                const dd = diaFecha.toString().padStart(2, "0");
                const mm = (fechaObj2.getMonth() + 1)
                  .toString()
                  .padStart(2, "0");
                const yy = fechaObj2.getFullYear().toString().slice(-2);
                const fechaFormateada = `${dd}/${mm}/${yy} (${nombreDia})`;

                let observaciones = "";
                let centroSecundario = null;
                if (
                  !esDescanso &&
                  !registroAusencia &&
                  registrosDia.length > 0
                ) {
                  const nombresDeCentroDelDia = registrosDia
                    .map((reg) => {
                      let nombre = reg.centroNombre;
                      if (
                        (!nombre ||
                          typeof nombre !== "string" ||
                          nombre.trim() === "") &&
                        reg.centro &&
                        typeof reg.centro.centroNombre === "string" &&
                        reg.centro.centroNombre.trim() !== ""
                      ) {
                        nombre = reg.centro.centroNombre;
                      }
                      return nombre &&
                        typeof nombre === "string" &&
                        nombre.trim() !== ""
                        ? nombre.trim()
                        : null;
                    })
                    .filter((nombre) => nombre !== null);

                  const centrosUnicosDiferentes = [
                    ...new Set(
                      nombresDeCentroDelDia.filter((cn) => cn !== sedePrincipal)
                    ),
                  ];

                  if (centrosUnicosDiferentes.length > 0) {
                    centroSecundario = centrosUnicosDiferentes.join(", ");
                  }
                }

                let horaIngreso = "-";
                let horaSalida = "-";
                let horaRefrigerioInicio = "-";
                let horaRefrigerioFin = "-";
                let firmaTxt = "-";
                let firmaRefrigerio = "-";

                if (registroAusencia) {
                  horaIngreso = "-";
                  horaSalida = "-";
                  horaRefrigerioInicio = "-";
                  horaRefrigerioFin = "-";
                  observaciones = registroAusencia.motivo || "AUSENCIA";
                } else if (esDescanso) {
                  observaciones = registroDescanso.diaSemana || "DESCANSO";
                } else {
                  horaIngreso = registroIngreso
                    ? formatearHora(registroIngreso.fechaMarcacion)
                    : "-";

                  if (registroSalida) {
                    horaSalida = formatearHora(registroSalida.fechaMarcacion);

                    // Solo mostrar la fecha real entre paréntesis si la hora de salida es de madrugada (00:00-07:59)
                    let fechaSalidaReal = null;

                    // Obtener la hora de salida del campo fechaMarcacion
                    const horaSalidaStr = registroSalida.fechaMarcacion || "";
                    const horaSalidaNum =
                      parseInt(horaSalidaStr.split(":")[0]) || 0;

                    // Solo mostrar fecha si es una salida de madrugada (00:00-07:59)
                    if (
                      horaSalidaNum >= 0 &&
                      horaSalidaNum < 8 &&
                      registroSalida.timestamp &&
                      typeof registroSalida.timestamp.toDate === "function"
                    ) {
                      let fechaJornada = registroSalida.fechaJornada || fecha;
                      const fechaSalidaObj = registroSalida.timestamp.toDate();
                      const [anioJ, mesJ, diaJ] = fechaJornada.split("-");
                      const fechaJornadaObj = new Date(
                        parseInt(anioJ),
                        parseInt(mesJ) - 1,
                        parseInt(diaJ)
                      );

                      // Solo agregar fecha si realmente es diferente a la fecha de jornada
                      if (
                        fechaSalidaObj.getFullYear() !==
                          fechaJornadaObj.getFullYear() ||
                        fechaSalidaObj.getMonth() !==
                          fechaJornadaObj.getMonth() ||
                        fechaSalidaObj.getDate() !== fechaJornadaObj.getDate()
                      ) {
                        const dd = fechaSalidaObj
                          .getDate()
                          .toString()
                          .padStart(2, "0");
                        const mm = (fechaSalidaObj.getMonth() + 1)
                          .toString()
                          .padStart(2, "0");
                        const yy = fechaSalidaObj
                          .getFullYear()
                          .toString()
                          .slice(-2);
                        fechaSalidaReal = `${dd}/${mm}/${yy}`;
                      }
                    }

                    // Solo agregar la fecha si es salida de madrugada
                    if (fechaSalidaReal) {
                      horaSalida += ` (${fechaSalidaReal})`;
                    }
                  }

                  horaRefrigerioInicio = registroRefrigerioInicio
                    ? formatearHora(registroRefrigerioInicio.fechaMarcacion)
                    : "-";
                  horaRefrigerioFin = registroRefrigerioFin
                    ? formatearHora(registroRefrigerioFin.fechaMarcacion)
                    : "-";
                  firmaTxt = firmaUrl
                    ? `<img src="${firmaUrl}" class="firma-img" crossorigin="anonymous" />`
                    : "-";
                  firmaRefrigerio =
                    (registroRefrigerioInicio || registroRefrigerioFin) &&
                    firmaUrl
                      ? `<img src="${firmaUrl}" class="firma-img" crossorigin="anonymous" />`
                      : "-";

                  if (centroSecundario) {
                    if (
                      observaciones &&
                      observaciones !== "AUSENCIA" &&
                      observaciones !== "DESCANSO"
                    ) {
                      observaciones += `. Centro laboral: ${centroSecundario}`;
                    } else if (!observaciones) {
                      observaciones = `Centro laboral: ${centroSecundario}`;
                    }
                  }
                }

                return `
                        <tr ${estiloFila}>
                          <td>${index + 1}</td>
                          <td>${fechaFormateada}</td>
                          <td>${horaIngreso}</td>
                          <td>${registroIngreso ? firmaTxt : "-"}</td>
                          <td>${horaSalida}</td>
                          <td>${registroSalida ? firmaTxt : "-"}</td>
                          <td>${horaRefrigerioInicio}</td>
                          <td>${horaRefrigerioFin}</td>
                          <td>${
                            registroRefrigerioInicio || registroRefrigerioFin
                              ? firmaRefrigerio
                              : "-"
                          }</td>
                          <td>${observaciones}</td>
                        </tr>
                      `;
              })
              .join("")}
            

          </tbody>
        </table>
      </div>
      <div class="footer">
        <p>El control de asistencia puede ser llevado en soporte físico o digital, adoptándose medidas
de seguridad que no permitan su adulteración, deterioro o pérdida.</p>
        <p>Fuente: artículo 3 del Decreto Supremo N.° 004-2006-TR.</p>
      </div>
    </div>
  </body>
  </html>
  `;
};

// ✅ Función para guardar PDF en Firebase (modificada con logs detallados)
const guardarPDFEnFirebase = async (pdfBlob, userData, año, mes, ruc) => {
  try {
    console.log(`🔄 Iniciando guardado para DNI ${userData.dni}, RUC: ${ruc}`); // ✅ Log inicial
    const fecha = new Date();
    const fechaStr = fecha
      .toISOString()
      .replace("T", "_")
      .replace(/:/g, "-")
      .split(".")[0];
    const nombreMes = getNombreMes(parseInt(mes));
    const storageRef = ref(
      storage,
      `asistencias/${userData.dni}/control_asistencia_${nombreMes}_${año}_${fechaStr}.pdf`
    );
    const snapshot = await uploadBytes(storageRef, pdfBlob);
    const downloadURL = await getDownloadURL(snapshot.ref);
    const nombresWorker = userData.nombres || userData.name || userData.nombre || "";
    const apellidosWorker = userData.apellidos || `${userData.apellidoPaterno || ''} ${userData.apellidoMaterno || ''}`.trim() || "";

    const docData = {
      dni: userData.dni,
      nombres: nombresWorker,
      apellidos: apellidosWorker,
      name: nombresWorker,
      created_at: serverTimestamp(),
      año: año,
      mes: parseInt(mes).toString(),  // ✅ Convertir a "8" para consistencia
      nombre_mes: nombreMes,
      pdf_url: downloadURL,
      ruc: ruc || userData.rucEmpresa || "",
    };
    const docId = `${userData.dni}_${año}_${mes}_${Date.now()}`;
    console.log(`📄 Datos a guardar en Firestore para ${docId}:`, docData); // ✅ Log antes de setDoc
    const registroRef = doc(db, "asistencia_reportes", docId);
    await setDoc(registroRef, docData);
    console.log(
      `✅ Reporte guardado exitosamente en Firestore para DNI ${userData.dni}: ${docId}`
    ); // ✅ Log de confirmación
    return downloadURL;
  } catch (error) {
    console.error(
      `❌ Error al guardar PDF en Firebase para DNI ${userData.dni}:`,
      error
    );
    throw error;
  }
};

// ✅ Función para generar PDF en web con limpieza segura DOM y multipágina
export const generatePDFWeb = async (html, userData, año, mes, ruc, descargar = true) => {
  const div = document.createElement("div");
  try {
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
      compress: true,
    });

    div.style.width = "210mm";
    div.style.position = "absolute";
    div.style.left = "-9999px";
    div.style.top = "-9999px";
    div.innerHTML = html;
    document.body.appendChild(div);

    await new Promise((resolve) => setTimeout(resolve, 100));

    const canvas = await html2canvas(div, {
      scale: 2,
      useCORS: true,
      logging: false,
      windowWidth: 595,
      windowHeight: 842,
    });

    const imgWidth = 190;
    const pageHeight = 277;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 10;

    const imgData = canvas.toDataURL("image/png");
    pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft >= 0) {
      position = heightLeft - imgHeight + 10;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    const pdfBlob = pdf.output("blob");
    await guardarPDFEnFirebase(pdfBlob, userData, año, mes, ruc);

    if (descargar) {
      const nombreMes = getNombreMes(parseInt(mes));
      pdf.save(`control-asistencia-${nombreMes}-${año}.pdf`);
    }
  } catch (error) {
    console.error("Error en generatePDFWeb:", error);
    throw error;
  } finally {
    if (div.parentNode) {
      document.body.removeChild(div);
    }
  }
};

// ✅ Modificar generateControlAsistencia para aceptar descargar
export const generateControlAsistencia = async (userDataInput, año, mes, ruc, descargar = true) => {
  try {
    if (!userDataInput || !userDataInput.dni || !año || !mes) {
      throw new Error("Faltan parámetros necesarios para generar el reporte");
    }

    let userData = { ...userDataInput };
    const cleanStr = (val) => {
      const s = String(val || "").trim();
      return s && s.toLowerCase() !== "n/a" && s.toLowerCase() !== "undefined" && s.toLowerCase() !== "null" ? s : "";
    };

    let nombresVal = cleanStr(userData.nombres) || cleanStr(userData.name) || cleanStr(userData.nombre);
    let apellidosVal = cleanStr(userData.apellidos) || `${cleanStr(userData.apellidoPaterno)} ${cleanStr(userData.apellidoMaterno)}`.trim();

    if (!nombresVal || !apellidosVal) {
      try {
        const dniLimpio = String(userData.dni).trim();
        const usersRef = collection(db, "users");
        let uSnap = await getDocs(query(usersRef, where("dni", "==", dniLimpio)));
        if (uSnap.empty && !isNaN(dniLimpio)) {
          uSnap = await getDocs(query(usersRef, where("dni", "==", parseInt(dniLimpio, 10))));
        }
        if (uSnap.empty) {
          const uDoc = await getDoc(doc(db, "users", dniLimpio));
          if (uDoc.exists()) uSnap = { empty: false, docs: [uDoc] };
        }
        if (!uSnap.empty) {
          const d = uSnap.docs[0].data() || {};
          const dNom = cleanStr(d.nombres) || cleanStr(d.name) || cleanStr(d.nombre);
          const dApe = cleanStr(d.apellidos) || `${cleanStr(d.apellidoPaterno)} ${cleanStr(d.apellidoMaterno)}`.trim();
          userData = {
            ...d,
            ...userData,
            nombres: nombresVal || dNom || "Trabajador",
            name: nombresVal || dNom || "Trabajador",
            apellidos: apellidosVal || dApe || "",
          };
        }
      } catch (e) {
        console.error("Error al enriquecer datos de trabajador para el PDF:", e);
      }
    }

    const companyData = await obtenerDatosEmpresa(ruc || userData.rucEmpresa);
    const firmaUrl = await obtenerFirmaTrabajador(userData.dni);
    const registrosPorFecha = await obtenerRegistrosMensuales(
      userData.dni,
      año,
      mes
    );
    const html = await createHTML(
      userData,
      registrosPorFecha,
      año,
      mes,
      firmaUrl,
      companyData
    );
    await generatePDFWeb(html, userData, año, mes, ruc, descargar); // ✅ Pasa descargar
    return `PDF generado para ${userData.dni}`;
  } catch (error) {
    console.error("Error generando reporte de asistencia:", error);
    throw error;
  }
};

// ✅ Función para generación masiva (optimizada con throttling para fluidez visual)
export const generateControlAsistenciaMasiva = async (
  usuarios,
  añoGenerar,
  mesGenerar,
  ruc,
  onProgress,
  descargar = true
) => {
  console.log(
    `🚀 Iniciando generación masiva para ${usuarios.length} usuarios, RUC: ${ruc}`
  );

  const año = añoGenerar;
  const mes = mesGenerar;
  const totalUsuarios = usuarios.length;
  let usuariosProcesados = 0;

  const batchSize = 2; // Tamaño de lote 2 para evitar congelar el UI thread de html2canvas
  const resultados = [];
  const errores = [];

  for (let i = 0; i < totalUsuarios; i += batchSize) {
    const batch = usuarios.slice(i, i + batchSize);
    console.log(`📦 Procesando lote ${Math.floor(i / batchSize) + 1} con ${batch.length} usuarios`);
    const promises = batch.map(async (userData) => {
      console.log(`👤 Procesando usuario ${userData.dni}`);
      try {
        const resultado = await generateControlAsistencia(
          userData,
          año,
          mes,
          ruc,
          descargar
        );
        console.log(`✅ Usuario ${userData.dni} completado`);
        resultados.push(resultado);
        usuariosProcesados++;
        const porcentaje = Math.round((usuariosProcesados / totalUsuarios) * 100);
        if (onProgress) onProgress(`Generado: ${userData.dni} (${porcentaje}%)`, porcentaje);
      } catch (error) {
        console.error(`❌ Error para ${userData.dni}:`, error);
        errores.push({ dni: userData.dni, error: error.message });
        usuariosProcesados++;
        const porcentaje = Math.round((usuariosProcesados / totalUsuarios) * 100);
        if (onProgress) onProgress(`Error en ${userData.dni}: ${error.message} (${porcentaje}%)`, porcentaje);
      }
    });
    await Promise.all(promises);
    // Micro-pausa para ceder control al navegador y refrescar barra de progreso de la UI
    await new Promise((resolve) => setTimeout(resolve, 40));
    console.log(`🏁 Lote ${Math.floor(i / batchSize) + 1} completado`);
  }

  console.log(
    `✅ Generación masiva completada: ${resultados.length} exitosos, ${errores.length} errores`
  );
  return { resultados, errores };
};

// ✅ Componente principal (modificado con barra de progreso más grande)
const GenerarReportePDF = ({ usuarios, usuariosSeleccionados, singleUser, ruc, año, mes, onReportesGenerados }) => {
  const [loading, setLoading] = React.useState(false);
  const [progress, setProgress] = React.useState("");
  const [progressPercent, setProgressPercent] = React.useState(0);

  const handleGenerarMasivo = async () => {
    const listToProcess = singleUser 
      ? [singleUser] 
      : (usuarios || usuariosSeleccionados);

    if (!Array.isArray(listToProcess) || listToProcess.length === 0) {
      alert("No hay usuarios disponibles para generar reportes con los filtros actuales.");
      return;
    }

    setLoading(true);
    setProgress("Iniciando generación...");
    setProgressPercent(0);

    try {
      const añoGenerar = año || new Date().getFullYear().toString();
      const mesGenerar = mes || (new Date().getMonth() + 1).toString().padStart(2, "0");

      const autoDownload = Boolean(singleUser);
      const { resultados, errores } = await generateControlAsistenciaMasiva(
        listToProcess,
        añoGenerar,
        mesGenerar,
        ruc || "",
        (msg, percent) => {
          setProgress(msg);
          setProgressPercent(percent);
        },
        autoDownload
      );

      setLoading(false);
      setProgressPercent(100);
      alert(
        `Completado: ${resultados.length} exitosos, ${errores.length} errores. Revisa la consola.`
      );

      // Llamar al callback para actualizar la vista
      if (onReportesGenerados) {
        onReportesGenerados();
      }
    } catch (error) {
      console.error("Error en generación masiva:", error);
      setLoading(false);
      setProgress("Error en la generación");
      alert("Error en la generación masiva. Revisa la consola.");
    }
  };

  return (
    <div>
      <Button
        variant={singleUser ? "outline-primary" : "primary"}
        size={singleUser ? "sm" : undefined}
        onClick={handleGenerarMasivo}
        disabled={loading}
        className="d-flex align-items-center gap-2"
      >
        {loading && <Spinner animation="border" size="sm" />}
        {loading ? "Generando..." : (singleUser ? "Generar PDF" : "Generar Reportes Masivos")}
      </Button>
      {loading && (
        <div className="mt-2 p-2 border rounded" style={{ width: singleUser ? '100%' : '600px', minWidth: singleUser ? '120px' : 'auto' }}>
          <ProgressBar now={progressPercent} label={`${progressPercent}%`} style={{ height: '20px', fontSize: '12px', width: '100%' }} />
          <p className="mt-1 text-muted small" style={{ fontSize: '11px' }}>{progress}</p>
        </div>
      )}
    </div>
  );
};

export default GenerarReportePDF;